import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import type { NotifItem } from "@/hooks/useNotifications";

interface NotificationMenuProps {
  open: boolean;
  onClose: () => void;
  items: NotifItem[];
  unreadCount: number;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
}

export function NotificationMenu({
  open,
  onClose,
  items,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
}: NotificationMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open, onClose]);

  if (!open) return null;

  function handleItemClick(item: NotifItem) {
    onMarkRead(item.id);
    if (item.navTo) {
      navigate(item.navTo);
      onClose();
    }
  }

  return (
    <div className="nf-menu" ref={ref}>
      <div className="qa-arrow" />
      <div className="nf-head">
        <span className="nf-head-title">
          알림{unreadCount > 0 && <span className="nf-head-count">{unreadCount}</span>}
        </span>
        {unreadCount > 0 && (
          <button type="button" className="nf-read-all" onClick={onMarkAllRead}>
            모두 읽음
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="nf-empty">
          <Icon name="bell" size={24} />
          <span>새 알림이 없습니다</span>
        </div>
      ) : (
        <div className="nf-list">
          {items.map((item) => (
            <button
              key={item.id}
              className={`nf-item ${item.read ? "is-read" : ""}`}
              onClick={() => handleItemClick(item)}
            >
              <span className={`nf-icon tone-${item.tone}`}>
                <Icon name={item.icon} size={16} />
              </span>
              <span className="nf-text">
                <span className="nf-title">{item.title}</span>
                <span className="nf-sub">{item.sub}</span>
                <span className="nf-time">{item.time}</span>
              </span>
              {!item.read && <span className="nf-unread" />}
            </button>
          ))}
        </div>
      )}

      <div className="qa-foot"><span>알림 설정</span><Icon name="settings" size={14} /></div>
    </div>
  );
}
